import { isAxiosError } from 'axios'

import { Steerage, SteerageProviderStatus } from '~/entities/steerage'

/**
 * Rejection message the API returns when the chosen specialty or service type has no
 * partners and no fallback to search instead.
 */
export const NO_PARTNERS_FOUND_MESSAGE = 'Try another specialty/service type, no partners found.'

/**
 * True when a failed steerage save is the "no partners found" rejection, which the caller
 * recovers from rather than surfacing as a generic failure. The message arrives as a
 * serializer-level ValidationError, which DRF renders as a JSON list on the response body.
 */
export const isNoPartnersFoundError = (error: unknown): boolean =>
  isAxiosError(error) &&
  error.response?.status === 400 &&
  Array.isArray(error.response.data) &&
  error.response.data[0] === NO_PARTNERS_FOUND_MESSAGE

/**
 * The server's message when a steerage save rejects the chosen service category (a retired
 * category), or null when the save failed for another reason.
 */
export const getServiceCategoryError = (error: unknown): string | null => {
  if (!isAxiosError<{ navigation_service_category_id?: string[] }>(error)) return null
  if (error.response?.status !== 400) return null
  return error.response.data?.navigation_service_category_id?.[0] ?? null
}

type SteerageProviderOnSteerage = NonNullable<Steerage['steerage_providers']>[number]

/**
 * The steerage provider whose prior auth stands for the steerage's, if any.
 *
 * Resolution is the member-selected provider, then the sole accepted provider. Prior auth lives on
 * the provider, so a steerage with no qualifying provider has none — there is no steerage-level
 * value to fall back to.
 */
const getEffectivePriorAuthProvider = (steerage: Steerage): SteerageProviderOnSteerage | null => {
  const providers = steerage.steerage_providers ?? []

  // A member selection wins whatever the provider's status is, which is what the backend's
  // `_get_effective_prior_auth_provider` does: it filters on `member_selected_at` across every
  // provider and applies the accepted-status filter only to the single-provider fallback. The
  // distinction is reachable — the ops review path saves only status and review fields, so a
  // provider rejected after the member picked it keeps its `member_selected_at`.
  const selected = providers.find(p => p.member_selected_at !== null)
  if (selected) return selected

  const accepted = providers.filter(p => p.status === SteerageProviderStatus.Accepted)
  return accepted.length === 1 ? (accepted[0] ?? null) : null
}

/** The steerage's effective prior auth, or null when no provider supplies one. */
export const getEffectivePriorAuthorization = (steerage: Steerage) =>
  getEffectivePriorAuthProvider(steerage)?.prior_authorization ?? null
